/**
 * Trunnion AI - Compare Selector
 * Dropdown selectors for picking two executions to compare side-by-side.
 * Fetches the execution list and allows filtering by workflow, then navigates
 * to /executions/compare?left=...&right=... on submit.
 */

import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { GitCompareArrows, Filter, ArrowRight } from 'lucide-react';
import { useExecutionList } from '@/hooks/queries/useExecutions';
import { useWorkflowList } from '@/hooks/queries/useWorkflows';
import { Card, CardContent } from '@/components/ui/Card';
import { Select } from '@/components/ui/Select';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { ROUTES } from '@/config/routes';
import { EXECUTION_STATUS_CONFIG } from '@/config/constants';
import type { Execution, ExecutionStatus } from '@/types';

/** Maps execution status to Badge variant for visual consistency. */
function statusVariant(
  status: ExecutionStatus,
): 'success' | 'error' | 'info' | 'warning' | 'default' {
  switch (status) {
    case 'completed':
      return 'success';
    case 'failed':
    case 'timed_out':
      return 'error';
    case 'running':
      return 'info';
    case 'pending':
    case 'paused':
    case 'waiting_approval':
      return 'warning';
    default:
      return 'default';
  }
}

/** Formats an ISO date string into a readable short format. */
function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Builds a human-readable label for an execution option. */
function executionLabel(exec: Execution): string {
  const shortId = exec.id.slice(0, 8);
  const statusLabel = EXECUTION_STATUS_CONFIG[exec.status]?.label ?? exec.status;
  return `${shortId} - ${exec.workflowName} [${statusLabel}] ${formatDate(exec.startedAt)}`;
}

interface CompareSelectorProps {
  /** Pre-selected left execution ID (from URL). */
  initialLeft?: string;
  /** Pre-selected right execution ID (from URL). */
  initialRight?: string;
}

export function CompareSelector({ initialLeft, initialRight }: CompareSelectorProps) {
  const navigate = useNavigate();

  const [workflowFilter, setWorkflowFilter] = useState('');
  const [leftId, setLeftId] = useState(initialLeft ?? '');
  const [rightId, setRightId] = useState(initialRight ?? '');

  // Fetch executions, optionally filtered by workflow
  const executionFilters = useMemo(
    () => ({
      pageSize: 50,
      ...(workflowFilter ? { workflowId: workflowFilter } : {}),
    }),
    [workflowFilter],
  );

  const {
    data: executionData,
    isLoading: executionsLoading,
    isError: executionsError,
  } = useExecutionList(executionFilters);

  const { data: workflowData, isLoading: workflowsLoading } = useWorkflowList();

  const executions = useMemo(() => executionData?.executions ?? [], [executionData]);

  // Build select options for the execution dropdowns
  const executionOptions = useMemo(
    () =>
      executions.map((exec) => ({
        value: exec.id,
        label: executionLabel(exec),
      })),
    [executions],
  );

  // Build select options for the workflow filter
  const workflowOptions = useMemo(() => {
    const workflows = workflowData?.workflows ?? workflowData ?? [];
    if (!Array.isArray(workflows)) return [];
    return [
      { value: '', label: 'All Workflows' },
      ...workflows.map((wf: { id: string; name: string }) => ({
        value: wf.id,
        label: wf.name,
      })),
    ];
  }, [workflowData]);

  const canCompare = leftId && rightId && leftId !== rightId;

  const handleCompare = () => {
    if (!canCompare) return;
    navigate(`${ROUTES.EXECUTION_COMPARE}?left=${leftId}&right=${rightId}`);
  };

  if (executionsLoading || workflowsLoading) {
    return (
      <div className="flex justify-center py-16">
        <LoadingSpinner label="Loading executions..." />
      </div>
    );
  }

  if (executionsError) {
    return (
      <EmptyState
        message="Failed to load executions"
        description="Unable to fetch the execution list. Please try again."
      />
    );
  }

  if (executions.length < 2) {
    return (
      <EmptyState
        icon={<GitCompareArrows className="h-12 w-12" />}
        message="Not enough executions"
        description="At least two executions are required to perform a comparison."
      />
    );
  }

  return (
    <Card padding="none">
      <div className="border-b border-trunnion-slate-700 px-5 py-4">
        <div className="flex items-center gap-2">
          <GitCompareArrows className="h-5 w-5 text-trunnion-gold-500" />
          <h3 className="text-sm font-semibold text-white">Select Executions to Compare</h3>
        </div>
        <p className="mt-1 text-xs text-trunnion-slate-400">
          Choose two executions to view a side-by-side comparison of steps, outputs, and token usage.
        </p>
      </div>

      <CardContent>
        {/* Workflow filter */}
        <div className="mb-5">
          <div className="flex items-center gap-1.5 mb-2">
            <Filter className="h-3.5 w-3.5 text-trunnion-slate-400" />
            <span className="text-xs font-medium text-trunnion-slate-300">Filter by Workflow</span>
          </div>
          <Select
            options={workflowOptions}
            value={workflowFilter}
            onChange={(e) => {
              setWorkflowFilter(e.target.value);
              setLeftId('');
              setRightId('');
            }}
            placeholder="All Workflows"
          />
        </div>

        {/* Execution selectors */}
        <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-4 items-end">
          {/* Left execution */}
          <div>
            <Select
              label="Left Execution"
              options={executionOptions}
              value={leftId}
              onChange={(e) => setLeftId(e.target.value)}
              placeholder="Select left execution..."
            />
            {leftId && (
              <ExecutionOptionPreview
                execution={executions.find((e) => e.id === leftId)}
              />
            )}
          </div>

          {/* Arrow separator */}
          <div className="hidden md:flex items-center justify-center pb-2">
            <ArrowRight className="h-5 w-5 text-trunnion-slate-500" />
          </div>

          {/* Right execution */}
          <div>
            <Select
              label="Right Execution"
              options={executionOptions}
              value={rightId}
              onChange={(e) => setRightId(e.target.value)}
              placeholder="Select right execution..."
            />
            {rightId && (
              <ExecutionOptionPreview
                execution={executions.find((e) => e.id === rightId)}
              />
            )}
          </div>
        </div>

        {/* Validation message */}
        {leftId && rightId && leftId === rightId && (
          <p className="mt-3 text-xs text-red-400">
            Please select two different executions to compare.
          </p>
        )}

        {/* Compare button */}
        <div className="mt-6 flex justify-end">
          <Button
            variant="primary"
            disabled={!canCompare}
            onClick={handleCompare}
            icon={<GitCompareArrows className="h-4 w-4" />}
          >
            Compare Executions
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Small inline preview card shown beneath a selector after a execution is chosen.
 */
function ExecutionOptionPreview({ execution }: { execution?: Execution }) {
  if (!execution) return null;

  return (
    <div className="mt-2 rounded-lg border border-trunnion-slate-700/50 bg-trunnion-slate-800/30 px-3 py-2 text-xs">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-mono text-trunnion-slate-300">{execution.id.slice(0, 8)}</span>
        <Badge variant={statusVariant(execution.status)} dot>
          {EXECUTION_STATUS_CONFIG[execution.status]?.label ?? execution.status}
        </Badge>
        <span className="text-trunnion-slate-400">{execution.workflowName}</span>
      </div>
      <div className="mt-1 flex items-center gap-3 text-trunnion-slate-500">
        <span>{formatDate(execution.startedAt)}</span>
        <span>{execution.totalTokensUsed.toLocaleString()} tokens</span>
        <span>{execution.steps.length} steps</span>
      </div>
    </div>
  );
}
